import { AlertTriangle, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useUsage, getUrgentLevel } from '@/hooks/useUsage'
import { useState } from 'react'
import { UpgradeModal } from './UpgradeModal'
import type { UsageLevel } from '@/types/database'

const bannerStyles: Record<UsageLevel, { bg: string; border: string; icon: typeof AlertTriangle }> = {
  ok: { bg: '', border: '', icon: AlertTriangle },
  warning: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: AlertTriangle },
  critical: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: AlertTriangle },
  blocked: { bg: 'bg-red-500/10', border: 'border-red-500/30', icon: XCircle },
}

function getUrgentMessage(level: UsageLevel): string {
  switch (level) {
    case 'blocked':
      return 'You have reached a resource limit. Upgrade your plan to continue.'
    case 'critical':
      return 'You are approaching a resource limit. Consider upgrading soon.'
    case 'warning':
      return 'You are nearing a resource limit.'
    default:
      return ''
  }
}

export function UsageBanner() {
  const { data: usage } = useUsage()
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const level = getUrgentLevel(usage)

  if (level === 'ok' || !usage) return null

  const style = bannerStyles[level]
  const Icon = style.icon

  return (
    <>
      <div
        role="alert"
        className={cn(
          'flex items-center gap-3 rounded-lg border px-4 py-3 mb-4',
          style.bg,
          style.border,
        )}
      >
        <Icon className={cn(
          'h-4 w-4 shrink-0',
          level === 'blocked' && 'text-red-500',
          level === 'critical' && 'text-orange-500',
          level === 'warning' && 'text-amber-500',
        )} />
        <p className="flex-1 text-sm">{getUrgentMessage(level)}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setUpgradeOpen(true)}
        >
          View Plans
        </Button>
      </div>
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </>
  )
}
